import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type { AnalyticsDashboardQuery, AnalyticsDashboardQueryVariables } from '../../graphql/queries/analyticsDashboard.generated';
import type { AnalyticsDashboard, AnalyticsDashboardDateRangePreset } from '../../graphql/__generated__/graphql';
import type {
  AnalyticsDashboardData,
  FrequencyInsight,
  Insight,
  InsightGroup,
  InsightScale,
  SingleSelectInsight,
} from './types';

export type AnalyticsFetchOptions = {
  signal?: AbortSignal;
};

export type AnalyticsFetcher = (
  dateRange: AnalyticsDashboardDateRangePreset,
  options?: AnalyticsFetchOptions,
) => Promise<AnalyticsDashboardData>;

// NOTE: keep this selection set in sync with analyticsDashboard.graphql (which
// drives the AnalyticsDashboardQuery type imported above) — codegen emits types
// only, not documents, so a change to one needs the other.
export const ANALYTICS_DASHBOARD_DOCUMENT = parse(/* GraphQL */ `
  query AnalyticsDashboard($dateRange: AnalyticsDashboardDateRangePreset!) {
    analyticsDashboard(dateRange: $dateRange) {
      accountId
      stats {
        key
        label
        value
        caption
      }
      groups {
        key
        label
        description
        variant
        insights {
          id
          kind
          title
          scale
          subtitle
          responseCount
          visitCount
          options {
            label
            count
            value
          }
          items {
            label
            count
          }
        }
      }
    }
  }
`);

const toInsight = (insight: AnalyticsDashboard['groups'][number]['insights'][number]): Insight => {
  if (insight.kind === 'frequency') {
    const frequencyInsight: FrequencyInsight = {
      id: insight.id,
      kind: 'frequency',
      title: insight.title,
      subtitle: insight.subtitle ?? undefined,
      visitCount: insight.visitCount,
      items: (insight.items ?? []).map(item => ({ label: item.label, count: item.count })),
    };
    return frequencyInsight;
  }

  const singleSelectInsight: SingleSelectInsight = {
    id: insight.id,
    kind: 'single_select',
    title: insight.title,
    scale: (insight.scale as InsightScale | null) ?? 'categorical',
    responseCount: insight.responseCount ?? 0,
    visitCount: insight.visitCount,
    options: (insight.options ?? []).map(option => ({ label: option.label, count: option.count, value: option.value ?? undefined })),
  };
  return singleSelectInsight;
};

const CUSTOM_SLOTS_DESCRIPTION = 'Extracted from recorded conversations.';

const toInsightGroup = (group: AnalyticsDashboard['groups'][number]): InsightGroup => ({
  key: group.key,
  label: group.label,
  description: group.key === 'custom-slots' ? CUSTOM_SLOTS_DESCRIPTION : (group.description ?? undefined),
  variant: group.variant as InsightGroup['variant'],
  insights: group.insights.map(toInsight),
});

const toAnalyticsDashboardData = (dashboard: AnalyticsDashboard): AnalyticsDashboardData => ({
  accountId: dashboard.accountId,
  stats: dashboard.stats.map(stat => ({
    key: stat.key,
    label: stat.label,
    value: stat.value,
    caption: stat.caption ?? undefined,
  })),
  groups: dashboard.groups.map(toInsightGroup),
});

// Single seam between the view and its data source — resolves the brokerage-
// scoped live query (account isolation is enforced server-side by that
// contract). Auth/env resolution mirrors authApi's authenticated call pattern.
export const fetchAnalyticsDashboard: AnalyticsFetcher = async (dateRange, options = {}) => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv() });
  const variables: AnalyticsDashboardQueryVariables = { dateRange };
  const result = await client.request<AnalyticsDashboardQuery>({
    document: ANALYTICS_DASHBOARD_DOCUMENT,
    variables,
    signal: options.signal,
  });
  return toAnalyticsDashboardData(result.analyticsDashboard);
};
